import { createRoute, Link, Outlet, useLocation, useNavigate } from '@tanstack/react-router'
import { rootRoute } from '../__root'
import { useAuthState } from '@/lib/auth-state'
import { useEffect } from 'react'
import { Building2, DatabaseBackup, Shield, Wrench } from 'lucide-react'
import { cn } from '@/lib/utils'

export const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin',
  component: AdminLayout,
})

const sidebarLinks = [
  { label: 'Organizations', to: '/admin', icon: Building2 },
  { label: 'Tools', to: '/admin/tools', icon: Wrench },
  { label: 'Backups', to: '/admin/backups', icon: DatabaseBackup },
] as const

function AdminLayout() {
  const { me, status } = useAuthState()
  const navigate = useNavigate()
  const { pathname } = useLocation()

  useEffect(() => {
    if (status === 'loading') return
    if (!me?.isSuperAdmin) {
      void navigate({ to: '/', replace: true })
    }
  }, [me, status, navigate])

  if (status === 'loading' || !me?.isSuperAdmin) {
    return null
  }

  return (
    <div className="flex h-screen bg-background">
      {/* Admin sidebar */}
      <aside className="w-64 border-r border-border bg-sidebar flex flex-col">
        <div className="p-3 border-b border-sidebar-border">
          <Link
            to="/"
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
          >
            ← Back to HUB
          </Link>
        </div>
        <div className="px-4 pt-4 pb-2">
          <div className="flex items-center gap-2 text-sidebar-foreground">
            <Shield className="h-5 w-5" />
            <h1 className="text-lg font-bold">Super Admin</h1>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {sidebarLinks.map((link) => {
            const isActive = pathname === link.to
            return (
              <Link
                key={link.to}
                to={link.to}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                  isActive &&
                    'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground'
                )}
              >
                <link.icon className="h-4 w-4 flex-shrink-0" />
                {link.label}
              </Link>
            )
          })}
        </nav>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  )
}
